import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Users, UserCheck, UserX, Plane, Clock, FileEdit, AlertCircle, Circle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  updateEmployeeAttendance,
  getAttendanceOverview,
  addLateArrival,
  fetchAttendanceTrend,
} from "@/services/attendanceService";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { formatTime } from "@/lib/utils";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext, PaginationEllipsis } from "@/components/ui/pagination";
import { usePermission } from "@/contexts/RoleAccessContext";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

const AttendanceDashboard = () => {
  const [department, setDepartment] = useState("all");
  const [shift, setShift] = useState("all");
  const [currentAttendaceData, setCurrentAttendaceData] = useState([]);
  const [AttendanceSummaryData, setattendanceSummaryData] = useState([
    {
      totalEmployees: 0,
      presentToday: 0,
      absentToday: 0,
      onLeave: 0,
      lateArrivals: 0,
      regularizationPending: 0,
    },
  ]);
  const [attendanceTrend, setAttendanceTrend] = useState([]);
  const [departmentAttendance, setDepartmentAttendance] = useState([]);
  const [lateArrivalForm, setLateArrivalForm] = useState({
    employee_id: "",
    date: new Date().toISOString().split("T")[0],
    actual_check_in: "",
    expected_check_in: "",
    reason: "",
  });
  const [trendRange, setTrendRange] = useState("7d");
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterData, setFilterData] = useState({
    department: [],
    shift: [],
  });  const [showLateArrivalDialog, setShowLateArrivalDialog] = useState(false);
  const [employees, setEmployees] = useState([]);
  const { hasFeature } = useSubscription();
  const hasDepartmentFeature = hasFeature("department_mapping");
  const canManageOvertime = hasFeature("overtime_management");
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const totalItems = currentAttendaceData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const { can } = usePermission();

  useEffect(() => {
    setPage(1);
  }, [department, shift]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedAttendance = React.useMemo(() => {
    const start = (page - 1) * pageSize;
    return currentAttendaceData.slice(start, start + pageSize);
  }, [currentAttendaceData, page, pageSize]);

  const buildPages = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages = new Set([1, total, current, current - 1, current + 1]);
    const cleaned = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

    const result = [];
    for (let i = 0; i < cleaned.length; i++) {
      result.push(cleaned[i]);
      if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };
  useEffect(() => {
    getAttendanceSummary(true);
  }, []);

  const getAttendanceSummary = async (showPageLoader = false) => {
    if (showPageLoader) setInitialLoading(true);
    try {
      const response = await getAttendanceOverview();
      console.log("getAttendanceOverview----", response.data);

      setCurrentAttendaceData(response.data.employees);
      setEmployees(response.data.employees);
      setattendanceSummaryData({
        totalEmployees: response.data.AttendanceSummaryData.total_active,
        presentToday: response.data.AttendanceSummaryData.present,
        absentToday: response.data.AttendanceSummaryData.absent,
        onLeave: response.data.AttendanceSummaryData.leave,
        lateArrivals: response.data.AttendanceSummaryData.late,
        regularizationPending: response.data.AttendanceSummaryData.regularizationPending,
      });
      setAttendanceTrend(response.data.attendanceTrend);
      setDepartmentAttendance(response.data.departmentAttendance);
      setFilterData({
        department: response.data.department || [],
        shift: response.data.shifts || [],
      });
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load attendance summary"));
      }
    } finally {
      if (showPageLoader) setInitialLoading(false);
    }
  };

  const handleLateArrivalSubmit = async (e) => {
    e.preventDefault();
    try {
      const now = new Date();
      const response = await addLateArrival({...lateArrivalForm, timezoneOffset: now.getTimezoneOffset()});
      toast.success("Late arrival recorded successfully");
      setShowLateArrivalDialog(false);
      setLateArrivalForm({
        employee_id: "",
        date: new Date().toISOString().split("T")[0],
        actual_check_in: "",
        expected_check_in: "",
        reason: "",
      });
      fetchLateArrivals();
      getAttendanceSummary();
    } catch (error) {
      console.error("Error recording late arrival:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Failed to record late arrival";
      toast.error(
        typeof errorMessage === "string"
          ? errorMessage
          : JSON.stringify(errorMessage),
      );
    }
  };
  const fetchLateArrivals = async () => {
    try {
    } catch (error) {
      toast.error("Failed to fecth late arrivals");
    }
  };

  // AttendanceSummary
  const summaryCards = [
    {
      title: "Total Employees",
      value: AttendanceSummaryData.totalEmployees,
      icon: Users,
      color: "text-blue-600 dark:text-blue-400",
    },
    {
      title: "Present Today",
      value: AttendanceSummaryData.presentToday,
      icon: UserCheck,
      color: "text-primary",
    },
    {
      title: "Absent Today",
      value: AttendanceSummaryData.absentToday,
      icon: UserX,
      color: "text-red-600 dark:text-red-400",
    },
    {
      title: "On Leave",
      value: AttendanceSummaryData.onLeave,
      icon: Plane,
      color: "text-purple-600 dark:text-purple-400",
    },
    {
      title: "Late Arrivals",
      value: AttendanceSummaryData.lateArrivals,
      icon: Clock,
      color: "text-orange-600 dark:text-orange-400",
    },
    {
      title: "Pending Requests",
      value: AttendanceSummaryData.regularizationPending,
      icon: FileEdit,
      color: "text-yellow-600 dark:text-yellow-400",
    },
  ];

  const COLORS = [
    "hsl(160, 70%, 40%)",
    "hsl(200, 70%, 45%)",
    "hsl(280, 60%, 50%)",
    "hsl(30, 80%, 55%)",
    "hsl(350, 70%, 50%)",
    "hsl(120, 50%, 45%)",
  ];
  function formatLateByMinutes(value) {
    const mins = Number(value ?? 0);
    if (!Number.isFinite(mins) || mins <= 0) return null;

    const rounded = Math.floor(mins);
    return `Late by ${rounded} ${rounded === 1 ? "min" : "mins"}`;
  }

  function formatLateByMinutes(value) {
    const mins = Number(value ?? 0);
    if (!Number.isFinite(mins) || mins <= 0) return null;

    const rounded = Math.floor(mins);
    return `Late by ${rounded} ${rounded === 1 ? "min" : "mins"}`;
  }

  const RANGE_OPTIONS = [
    { value: "7d", label: "Last 7 days" },
    { value: "15d", label: "Last 15 days" },
    { value: "1m", label: "Last 1 month" },
    { value: "6m", label: "Last 6 months" },
  ];

  // 1) Define your handler (above return)
  const handleTrendRangeChange = async (value) => {
    try {
      setTrendRange(value);
      const response = await fetchAttendanceTrend(value);
      setAttendanceTrend(response.attendanceTrend);
    } catch (error) {
      console.log(error);
    }
  };

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-80" />
          </div>
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-5 space-y-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Attendance Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Real-time attendance monitoring and analytics
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex space-x-2">
            {/* <Button
              variant="outline"
              onClick={() => setShowMarkAttendanceDialog(true)}
              className="dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Mark Attendance
            </Button> */}
            <Button
              variant="outline"
              disabled={!can("attendance_reports", "create_late")}
              onClick={() => {
                if (!can("attendance_reports", "create_late", true)) return;
                setShowLateArrivalDialog(true);
              }}
              className="dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <AlertCircle className="w-4 h-4 mr-2" />
              Record Late Arrival
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.title}
              className="hover:shadow-lg transition-shadow"
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </CardTitle>
                <Icon className={`h-5 w-5 ${card.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">
                  {card.value || 0}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Section */}
      <div
        className={`grid gap-6 ${hasDepartmentFeature ? "lg:grid-cols-2" : ""}`}
      >
        <Card>
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <CardTitle>Attendance Trend</CardTitle>
              <CardDescription>
                Present vs Absent employees for the selected period
              </CardDescription>
            </div>

            <div className="w-full sm:w-[180px]">
              <Select value={trendRange} onValueChange={handleTrendRangeChange}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  {RANGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>

          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={attendanceTrend}>
                <defs>
                  <linearGradient
                    id="presentGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#3194a0" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3194a0" stopOpacity={0} />
                  </linearGradient>

                  <linearGradient
                    id="absentGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="5%"
                      stopColor="hsl(0, 70%, 50%)"
                      stopOpacity={0.4}
                    />
                    <stop
                      offset="95%"
                      stopColor="hsl(0, 70%, 50%)"
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" className="text-xs" />
                <YAxis className="text-xs" />

                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                />

                <Legend />

                <Area
                  type="monotone"
                  dataKey="present"
                  stroke="#3194a0"
                  strokeWidth={2}
                  fill="url(#presentGradient)"
                  name="Present"
                />

                <Area
                  type="monotone"
                  dataKey="absent"
                  stroke="hsl(0, 70%, 50%)"
                  strokeWidth={2}
                  fill="url(#absentGradient)"
                  name="Absent"
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        {hasDepartmentFeature && (
          <Card>
            <CardHeader>
              <CardTitle>Daily Department Attendance Overview</CardTitle>
              <CardDescription>
                Current attendance by department
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentAttendance}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="department"
                    className="text-xs"
                    angle={-15}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--background))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="present"
                    fill="hsl(160, 70%, 40%)"
                    name="Present"
                    radius={[8, 8, 0, 0]}
                  />
                  <Bar
                    dataKey="absent"
                    fill="hsl(0, 70%, 50%)"
                    name="Absent"
                    radius={[8, 8, 0, 0]}
                  />
                  <Bar
                    dataKey="leave"
                    fill="hsl(30, 70%, 50%)"
                    name="Leave"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Today's Attendance Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Today's Attendance</CardTitle>
              <CardDescription>
                Real-time employee attendance status
              </CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              {hasDepartmentFeature && (
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>

                    {filterData.department.map((dep) => (
                      <SelectItem key={dep._id} value={dep._id}>
                        <div className="flex items-center space-x-2">
                          <span>{dep.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Shift" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {filterData.shift.map((status) => {
                    return (
                      <SelectItem key={status._id} value={status._id}>
                        <div className="flex items-center space-x-2">
                          <span>{status.name}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Employee
                  </th>
                  {hasDepartmentFeature && (
                    <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                      Department
                    </th>
                  )}
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Status
                  </th>

                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    In Time
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Out Time
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Shift
                  </th>
                  {canManageOvertime && (
                    <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                      OT
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {/* todayAttendance */}
                {paginatedAttendance.map((record) => (
                  <tr
                    key={record.id}
                    className="border-b border-border hover:bg-muted/50 transition-colors"
                  >
                    <td className="py-3 px-4 text-sm font-medium text-foreground">
                      {record.name || "-"}
                    </td>
                    {hasDepartmentFeature && (
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {record.department || "-"}
                      </td>
                    )}
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      <Badge
                        variant="outline"
                        className={
                          record.status === "Present"
                            ? "bg-primary/10 text-primary border-primary/20"
                            : record.status === "Absent"
                              ? "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
                              : "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
                        }
                      >
                        {/* ICON */}
                        {record.status === "Present" && (
                          <CheckCircle className="h-3 w-3 mr-1" />
                        )}

                        {record.status === "Absent" && (
                          <Circle className="h-3 w-3 mr-1" />
                        )}

                        {record.status || "-"}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {(() => {
                        const attendance = record.todayAttendance;

                        const lateByMinutes = Number(
                          attendance?.late_by_minutes ?? 0,
                        );
                        const isLate = lateByMinutes > 0;

                        const expectedStart =
                          attendance?.shift?.start_time ??
                          record.shift?.startTime ??
                          record.shift?.start_time;

                        // ✅ PUNCHED IN
                        if (attendance?.punch_in) {
                          return (
                            <div className="flex flex-col leading-tight">
                              <span
                                className={
                                  isLate
                                    ? "font-semibold text-orange-600 dark:text-orange-400"
                                    : "font-medium text-foreground"
                                }
                              >
                                {formatTime(attendance.punch_in)}
                              </span>

                              {isLate && (
                                <span className="text-xs text-orange-500 dark:text-orange-400">
                                  {formatLateByMinutes(lateByMinutes)}{" "}
                                </span>
                              )}
                            </div>
                          );
                        }

                        // ✅ NOT PUNCHED IN (show expected + late if any)
                        if (expectedStart) {
                          return (
                            <div className="flex flex-col leading-tight">
                              <span className="text-muted-foreground">
                                {expectedStart}
                              </span>

                              {isLate ? (
                                <span className="text-xs text-orange-500 dark:text-orange-400">
                                  Late by {lateByMinutes} min
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  Expected
                                </span>
                              )}
                            </div>
                          );
                        }

                        return <span className="text-muted-foreground">—</span>;
                      })()}
                    </td>

                    <td className="py-3 px-4 text-sm">
                      {record.todayAttendance.punch_out ? (
                        <div className="flex flex-col leading-tight">
                          {/* Actual Punch Out */}
                          <span className="font-medium text-foreground">
                            {formatTime(record.todayAttendance.punch_out)}
                          </span>

                          <span className="text-xs text-muted-foreground">
                            Checked out
                          </span>
                        </div>
                      ) : record.shift?.endTime ? (
                        <div className="flex flex-col leading-tight">
                          {/* Expected Shift End */}
                          <span className="text-muted-foreground">
                            {record.shift.endTime}
                          </span>

                          <span className="text-xs text-muted-foreground">
                            Expected
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">{record.shift?.name || "-"}</td>
                    {canManageOvertime && (
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {record.overtimeHours > 0
                          ? `${record.overtimeHours}h`
                          : "-"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {/* Late Arrival Dialog */}
      <Dialog
        open={showLateArrivalDialog}
        onOpenChange={setShowLateArrivalDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Late Arrival</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLateArrivalSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Employee *
              </Label>

              <Select
                value={lateArrivalForm.employee_id}
                onValueChange={(employeeId) => {
                  const selectedEmp = employees.find(
                    (e) => e.employee_id === employeeId,
                  );

                  setLateArrivalForm({
                    ...lateArrivalForm,
                    employee_id: employeeId,
                    expected_check_in: selectedEmp?.shift?.startTime,
                  });
                }}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>

                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.name} ({emp.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Date *</Label>
              <Input
                type="date"
                value={lateArrivalForm.date}
                onChange={(e) =>
                  setLateArrivalForm({
                    ...lateArrivalForm,
                    date: e.target.value,
                  })
                }
                required
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Actual Check-in Time *
              </Label>
              <Input
                type="time"
                value={lateArrivalForm.actual_check_in}
                onChange={(e) =>
                  setLateArrivalForm({
                    ...lateArrivalForm,
                    actual_check_in: e.target.value,
                  })
                }
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Expected check-in: {lateArrivalForm.expected_check_in}
              </p>
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Reason (Optional)
              </Label>
              <Textarea
                value={lateArrivalForm.reason}
                onChange={(e) =>
                  setLateArrivalForm({
                    ...lateArrivalForm,
                    reason: e.target.value,
                  })
                }
                placeholder="e.g., Traffic, Personal emergency, etc."
                rows={3}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowLateArrivalDialog(false);
                  setLateArrivalForm({
                    employee_id: "",
                    date: new Date().toISOString().split("T")[0],
                    actual_check_in: "",
                    reason: "",
                  });
                }}
              >
                Cancel
              </Button>
              <Button type="submit">Record Late Arrival</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Mark Attendance Dialog */}
     
      {currentAttendaceData.length > 0 && (
        <div className="flex justify-end mt-4">
          <Pagination className="mx-0 justify-end">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={page === 1}
                  className={page === 1 ? "pointer-events-none opacity-50" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.max(1, p - 1));
                  }}
                />
              </PaginationItem>

              {buildPages(page, totalPages).map((p, idx) => {
                if (p === "...") {
                  return (
                    <PaginationItem key={`e-${idx}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  );
                }

                return (
                  <PaginationItem key={p}>
                    <PaginationLink
                      href="#"
                      isActive={p === page}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage(p);
                      }}
                    >
                      {p}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={page === totalPages}
                  className={
                    page === totalPages ? "pointer-events-none opacity-50" : ""
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default AttendanceDashboard;
